import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import AlertsService from 'cio/services/alerts';
import EnvironmentService from 'cio/services/environment';
import AccountService from 'cio/services/account';
import RouterService from '@ember/routing/router-service';
import HydraService from 'cio/services/hydra';
import DS from 'ember-data';
import config from 'cio/config/environment';
import { task, timeout } from 'ember-concurrency';
import { announceRefreshWorkspaces } from '@customerio/hydra-nav-state';
import ApiService from 'cio/services/api';
import AuthService from 'cio/services/auth';

export default class WorkspaceSettingsConfirmDeleteController extends Controller {
  @service account!: AccountService;
  @service alerts!: AlertsService;
  @service api!: ApiService;
  @service auth!: AuthService;
  @service environment!: EnvironmentService;
  @service hydra!: HydraService;
  @service router!: RouterService;
  @service store!: DS.Store;

  @action
  cancel() {
    this.router.transitionTo('settings.workspaces');
  }

  deleteWorkspaceViaHydra = task({ drop: true }, async () => {
    let environmentId = this.environment.current.id;

    if (environmentId == null) {
      return;
    }

    await this.api.delete(`workspaces/${environmentId}`, {
      pathScope: 'hydra_account',
      ignoreErrors: true,
    });

    let workspaceDeleted = false;

    // Wait for the Journeys workspace to be deleted
    while (!workspaceDeleted) {
      try {
        await this.store.findRecord('environment', environmentId);
        // If the workspace still exists, wait and try again
        await timeout(config.APP.workspacePollDelay);
      } catch (e) {
        // When a workspace delete is initiated, all users are immediately
        // removed from the workspace - the findRecord API call would then
        // return 403. This is enough for us to know that the workspace is being
        // deleted correctly, so we redirect.
        // Otherwise, we're also adding a 404 check to redirect if the workspace
        // delete is instant.
        if (e.errors?.[0]?.status === '403' || e.errors?.[0]?.status === '404') {
          workspaceDeleted = true;
        } else {
          // If there's an unexpected error, rethrow it
          throw e;
        }
      }
    }

    announceRefreshWorkspaces();

    // Remove the workspace from the store, and force user models to recalculate
    // their accessible/default environments.
    // Usually, a destroyRecord would take care of this, but here we're deleting
    // the environment with a DELETE request that's not connected to the Ember
    // Data store.
    // We also can't yet refresh the account and user models, because at this
    // point we still haven't updated the "current" environment nor transitioned
    // into a different route - referencing the just-deleted environment could
    // cause issues.
    this.store.peekRecord('environment', environmentId)?.unloadRecord();
    this.store
      .peekAll('user')
      .toArray()
      .forEach((user) => {
        // Force computed properties based on "accessibleEnvironmentIds" to
        // recompute (the user model loops through the environment IDs and pulls
        // in only store records that exist, but the computed properties don't
        // automatically recomputed when just the store updates)
        user.notifyPropertyChange('accessibleEnvironmentIds');
      });
  });

  deleteWorkspaceViaServices = task({ drop: true }, async () => {
    await this.environment.current.destroyRecord();
  });

  submit = task({ drop: true }, async () => {
    let { name } = this.environment.current;

    if (this.hydra.isHydraEnabled) {
      await this.deleteWorkspaceViaHydra.perform();
    } else {
      await this.deleteWorkspaceViaServices.perform();
    }

    this.alerts.success('environment.delete.success', `${name} workspace deleted`);
    this.environment.set('current', this.account.current.currentUser.defaultEnvironment);

    this.router.transitionTo('settings.workspaces');
  });
}
